/**
 * Functions to transform geometry with Mat4's
 * @module
 */
import { aabb2ByPoints } from "./aabb2";
import type { AABB2 } from "./aabb2Types";
import type { BRep, Face } from "./brep";
import { faceCopy, faceRepairLoopDirections } from "./brep";
import { DIST_EPSILON } from "./core";
import { geomToFaces } from "./geom";
import GeomTagGroup from "./geomTagGroup";
import type { Mat3 } from "./mat3";
import { mat3Dot } from "./mat3";
import type { Mat4 } from "./mat4";
import { mat4Dot, mat4DotSub3, mat4LinearDeterminant3 } from "./mat4";
import { planeReverse } from "./plane";
import type { Plane } from "./planeTypes";
import { polyLine2ScaleX } from "./polyLine2";
import { vec3Len } from "./vec3";

export function transformBRep(brep: BRep, mat: Mat4) {
  brep.faces.forEach((face) => {
    transformFace(face, mat);
  });

  return brep;
}

export function transformAABB2(bb: AABB2, mat: Mat4): AABB2 {
  const [x0, y0] = mat4Dot(mat, bb.minX, bb.minY, 0);
  const [x1, y1] = mat4Dot(mat, bb.maxX, bb.maxY, 0);
  const [x2, y2] = mat4Dot(mat, bb.maxX, bb.minY, 0);
  const [x3, y3] = mat4Dot(mat, bb.minX, bb.maxY, 0);

  return aabb2ByPoints([
    [x0, y0],
    [x1, y1],
    [x2, y2],
    [x3, y3],
  ]);
}

export function transformAABB2ByMat3(bb: AABB2, mat: Mat3): AABB2 {
  const [x0, y0] = mat3Dot(mat, bb.minX, bb.minY);
  const [x1, y1] = mat3Dot(mat, bb.maxX, bb.maxY);
  const [x2, y2] = mat3Dot(mat, bb.maxX, bb.minY);
  const [x3, y3] = mat3Dot(mat, bb.minX, bb.maxY);

  return aabb2ByPoints([
    [x0, y0],
    [x1, y1],
    [x2, y2],
    [x3, y3],
  ]);
}

export function transformFace(face: Face, mat: Mat4): Face {
  transformPlane(face.plane, mat);
  const uLen = vec3Len(face.plane.uX, face.plane.uY, face.plane.uZ);
  const vLen = vec3Len(face.plane.vX, face.plane.vY, face.plane.vZ);
  if (Math.abs(uLen - 1.0) > DIST_EPSILON || Math.abs(vLen - 1.0) > DIST_EPSILON) {
    for (const loop of face.loops) {
      for (let i = 0, vertCount = loop.length; i < vertCount; i += 2) {
        loop[i] = loop[i] * uLen;
        loop[i + 1] = loop[i + 1] * vLen;
      }
    }
    face.plane.uX /= uLen;
    face.plane.uY /= uLen;
    face.plane.uZ /= uLen;
    face.plane.vX /= vLen;
    face.plane.vY /= vLen;
    face.plane.vZ /= vLen;
  }
  // Reverse the plane and loops if the transform is a mirror
  if (mat4LinearDeterminant3(mat) < 0) {
    planeReverse(face.plane);
    for (const loop of face.loops) {
      polyLine2ScaleX(loop, -1);
    }
    faceRepairLoopDirections(face);
  }
  return face;
}

export function transformPlane(plane: Plane, mat: Mat4) {
  const { originX, originY, originZ, uX, uY, uZ, vX, vY, vZ } = plane;
  const [x, y, z] = mat4Dot(mat, originX, originY, originZ);
  const [ux, uy, uz] = mat4DotSub3(mat, uX, uY, uZ);
  const [vx, vy, vz] = mat4DotSub3(mat, vX, vY, vZ);

  plane.originX = x;
  plane.originY = y;
  plane.originZ = z;

  plane.uX = ux;
  plane.uY = uy;
  plane.uZ = uz;

  plane.vX = vx;
  plane.vY = vy;
  plane.vZ = vz;

  return plane;
}

export function transformVerts(vertices: Float32Array | Float64Array | number[], mat: Mat4) {
  for (let i = 0, ic = vertices.length / 3; i < ic; ++i) {
    const [x, y, z] = mat4Dot(mat, vertices[i * 3], vertices[i * 3 + 1], vertices[i * 3 + 2]);
    vertices[i * 3] = x;
    vertices[i * 3 + 1] = y;
    vertices[i * 3 + 2] = z;
  }
}

/**
 * Transforms a GeomTagGroup by extracting faces from each geometry item, transforming them, and creating a new GeomTagGroup.
 */
export function transformGeomTagGroup<T>(geomTagGroup: GeomTagGroup<T>, transform: Mat4): GeomTagGroup<T> {
  const transformed = new GeomTagGroup<T>();

  for (let i = 0; i < geomTagGroup.geom.length; i++) {
    const tag = geomTagGroup.tags[i];

    // Extract faces from this geometry item.
    const faces = geomToFaces(geomTagGroup.geom[i]);

    // Transform each face and add it to the new group.
    for (const face of faces) {
      const transformedFace = transformFace(faceCopy(face), transform);
      transformed.add(transformedFace, tag);
    }
  }

  return transformed;
}
